import {serverDomain} from "../constants";
import {
  ComputedBillingRun,
  computedBillingRunSchema,
  BillingLineItemUpdate,
  BillingRunSource,
  BulkPriceUpdate,
} from "sharedlibs";
import KeycloakService from "../KeycloakService";

function authHeaders(): Record<string, string> {
  return {
    "Authorization": `Bearer ${KeycloakService.instance.getToken()}`,
    "Content-Type": "application/json",
  }
}

export async function getCurrentBilling(): Promise<ComputedBillingRun | null> {
  const response = await fetch(`${serverDomain}/billing/current`, {
    method: 'GET',
    headers: authHeaders(),
  })
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  const json = await response.json()
  if (json === null) return null
  return computedBillingRunSchema.parse(json)
}

export async function getBillingRunById(id: string): Promise<ComputedBillingRun> {
  const response = await fetch(`${serverDomain}/billing/${id}`, {
    method: 'GET',
    headers: authHeaders(),
  })
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  const json = await response.json()
  return computedBillingRunSchema.parse(json)
}

export async function updateLineItem(id: string, updates: BillingLineItemUpdate): Promise<void> {
  const response = await fetch(`${serverDomain}/billing/line-item/${id}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(updates),
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.error || `HTTP ${response.status}`)
  }
}

export async function bulkUpdatePrices(billingRunId: string, prices: BulkPriceUpdate): Promise<void> {
  const response = await fetch(`${serverDomain}/billing/${billingRunId}/bulk-prices`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(prices),
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.error || `HTTP ${response.status}`)
  }
}

export async function approveBilling(billingRunId: string, synchronizePricesToWeClapp: boolean): Promise<void> {
  const response = await fetch(`${serverDomain}/billing/${billingRunId}/approve`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({synchronizePricesToWeClapp}),
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.error || `HTTP ${response.status}`)
  }
}

export async function generateBilling(
  month: number, year: number, source: BillingRunSource = 'snapshot',
): Promise<void> {
  const response = await fetch(`${serverDomain}/billing/generate`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({month, year, source}),
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.error || `HTTP ${response.status}`)
  }
}

export async function cancelBilling(billingRunId: string): Promise<void> {
  const response = await fetch(`${serverDomain}/billing/${billingRunId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.error || `HTTP ${response.status}`)
  }
}

export async function retryLineItemPush(id: string): Promise<void> {
  const response = await fetch(`${serverDomain}/billing/line-item/${id}/push`, {
    method: 'POST',
    headers: authHeaders(),
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.error || `HTTP ${response.status}`)
  }
}

export async function retryAllFailedPushes(billingRunId: string): Promise<void> {
  const response = await fetch(`${serverDomain}/billing/${billingRunId}/push-failed`, {
    method: 'POST',
    headers: authHeaders(),
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.error || `HTTP ${response.status}`)
  }
}

// DEV-ONLY: seed billing run with test data (remove when no longer needed)
export async function seedBilling(): Promise<void> {
  const response = await fetch(`${serverDomain}/billing/seed`, {
    method: 'POST',
    headers: authHeaders(),
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.error || `HTTP ${response.status}`)
  }
}

export async function concludeBilling(billingRunId: string): Promise<void> {
  const response = await fetch(`${serverDomain}/billing/${billingRunId}/conclude`, {
    method: 'POST',
    headers: authHeaders(),
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.error || `HTTP ${response.status}`)
  }
}

export interface PastBillingRun {
  id: string
  month: number
  year: number
  status: string
  createdAt: string
  approvedAt: string | null
  approvedBy: string | null
  concludedAt: string | null
}

export async function getPastBillingRuns(): Promise<PastBillingRun[]> {
  const response = await fetch(`${serverDomain}/billing/past`, {
    method: 'GET',
    headers: authHeaders(),
  })
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  return response.json()
}


export async function checkSnapshot(month: number, year: number): Promise<boolean> {
  const response = await fetch(`${serverDomain}/billing/snapshot/check?month=${month}&year=${year}`, {
    method: 'GET',
    headers: authHeaders(),
  })
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  const json = await response.json()
  return json.hasSnapshot
}
